import {
  useState,
  useEffect,
  useRef,
  ReactNode,
  cloneElement,
  isValidElement,
  ReactElement,
} from "react";
import {
  format,
  addMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  setHours,
  setMinutes,
} from "date-fns";
import { enUS, tr } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Text } from "@core/text";
import { Input } from "@core/input";
import { Button } from "@core/button";

type DateTimePickerProps = {
  value: string;
  onChange: (date: string) => void;
  minDate?: Date;
  className?: string;
  placeholder?: string;
  baseComponent?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onFinish?: (date: Date) => void;
};

// Helper function to scroll parent to child element without scrolling the whole page
function scrollParentToChild(parent: HTMLElement, child: Element) {
  // Where is the parent on page
  const parentRect = parent.getBoundingClientRect();
  // What can you see?
  const parentViewableArea = {
    height: parent.clientHeight,
    width: parent.clientWidth,
  };

  // Where is the child
  const childRect = child.getBoundingClientRect();

  // Calculate position to center the child in the parent viewable area
  const centerPosition =
    parent.scrollTop +
    (childRect.top - parentRect.top) -
    (parentViewableArea.height / 2 - childRect.height / 2);

  // Smooth scrolling to the target position
  parent.scrollTo({
    top: centerPosition,
    behavior: "smooth",
  });
}

const DateTimePicker = ({
  value,
  onChange,
  minDate = new Date(),
  className = "",
  placeholder = "",
  baseComponent,
  isOpen: externalIsOpen,
  onOpenChange,
  onFinish,
}: DateTimePickerProps) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === "tr" ? tr : enUS;

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value) : null,
  );
  const [currentMonth, setCurrentMonth] = useState(
    startOfMonth(selectedDate || new Date()),
  );
  const [currentHour, setCurrentHour] = useState(
    selectedDate ? selectedDate.getHours() : 12,
  );
  const [currentMinute, setCurrentMinute] = useState(
    selectedDate ? Math.floor(selectedDate.getMinutes() / 5) * 5 : 0,
  );

  // Use either external or internal isOpen state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  // Set the isOpen state and call the onOpenChange callback if provided
  const setIsOpen = (nextOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
    }
    setInternalIsOpen(nextOpen);
  };

  const pickerRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  // Available hours and minutes
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      setCurrentMonth(startOfMonth(date));
      setCurrentHour(date.getHours());
      // Round to nearest 5min
      setCurrentMinute(Math.floor(date.getMinutes() / 5) * 5);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle window resize to check for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Scroll to selected time values when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourListRef.current) {
          const hourItem = hourListRef.current.querySelector(
            `[data-hour="${currentHour}"]`,
          );
          if (hourItem && hourListRef.current) {
            // Use the custom scroll function instead of scrollIntoView
            scrollParentToChild(hourListRef.current, hourItem);
          }
        }
        if (minuteListRef.current) {
          const minuteItem = minuteListRef.current.querySelector(
            `[data-minute="${currentMinute}"]`,
          );
          if (minuteItem && minuteListRef.current) {
            // Use the custom scroll function instead of scrollIntoView
            scrollParentToChild(minuteListRef.current, minuteItem);
          }
        }
      }, 100);
    }
  }, [isOpen]);

  const previousMonth = () => {
    setCurrentMonth(addMonths(currentMonth, -1));
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const selectDate = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(currentHour, currentMinute);
    setSelectedDate(newDate);
    onChange(newDate.toISOString());
  };

  const selectHour = (hour: number) => {
    setCurrentHour(hour);
    if (selectedDate) {
      const newDate = setHours(selectedDate, hour);
      setSelectedDate(newDate);
      onChange(newDate.toISOString());
    }

    // Scroll to the selected hour
    setTimeout(() => {
      if (hourListRef.current) {
        const hourItem = hourListRef.current.querySelector(
          `[data-hour="${hour}"]`,
        );
        if (hourItem && hourListRef.current) {
          scrollParentToChild(hourListRef.current, hourItem);
        }
      }
    }, 0);
  };

  const selectMinute = (minute: number) => {
    setCurrentMinute(minute);
    if (selectedDate) {
      const newDate = setMinutes(selectedDate, minute);
      setSelectedDate(newDate);
      onChange(newDate.toISOString());
    }

    // Scroll to the selected minute
    setTimeout(() => {
      if (minuteListRef.current) {
        const minuteItem = minuteListRef.current.querySelector(
          `[data-minute="${minute}"]`,
        );
        if (minuteItem && minuteListRef.current) {
          scrollParentToChild(minuteListRef.current, minuteItem);
        }
      }
    }, 0);
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const renderDays = () => {
    const firstDayOfMonth = startOfMonth(currentMonth).getDay();
    const daysInPrevMonth = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      daysInPrevMonth.push(
        <div key={`prev-${i}`} className="text-gray-400 p-2 text-center"></div>,
      );
    }

    return (
      <div className="grid grid-cols-7 gap-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div
            key={day}
            className="text-center font-medium text-gray-600 dark:text-gray-300 p-2 text-xs sm:text-sm"
          >
            {day}
          </div>
        ))}
        {daysInPrevMonth}
        {daysInMonth.map((day) => {
          const isDisabled = minDate && day < minDate;
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          return (
            <button
              key={day.toString()}
              onClick={() => !isDisabled && selectDate(day)}
              disabled={isDisabled}
              className={`
                p-2 rounded-md text-center transition-all min-h-[40px] min-w-[40px]
                ${isDisabled ? "text-gray-300 dark:text-gray-600 cursor-not-allowed" : "cursor-pointer"}
                ${isSelected ? "bg-indigo-500 text-white" : "hover:bg-gray-100 dark:hover:bg-gray-700"}
              `}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    );
  };

  const renderTimeSelector = () => {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-1 w-full">
          <div className="text-center font-medium mb-2 text-gray-700 dark:text-gray-300 text-sm">
            {t("common.time.hours")}
          </div>
          <div className="text-center font-medium mb-2 text-gray-700 dark:text-gray-300 text-sm">
            {t("common.time.minutes")}
          </div>
          {/* Hours Column */}
          <div className="bg-gray-100 dark:bg-gray-900 rounded-l-md overflow-hidden">
            <div
              ref={hourListRef}
              className="h-32 sm:h-40 overflow-y-auto scrollbar-hide flex flex-col"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {hours.map((hour) => (
                <div
                  key={hour}
                  data-hour={hour}
                  onClick={() => selectHour(hour)}
                  className={`
                    flex-shrink-0 p-3 h-10 flex items-center justify-center cursor-pointer
                    ${
                      currentHour === hour
                        ? "bg-indigo-500 text-white font-medium"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/70"
                    }
                  `}
                >
                  {hour.toString().padStart(2, "0")}
                </div>
              ))}
            </div>
          </div>

          {/* Minutes Column */}
          <div className="bg-gray-100 dark:bg-gray-900 rounded-r-md overflow-hidden">
            <div
              ref={minuteListRef}
              className="h-32 sm:h-40 overflow-y-auto scrollbar-hide flex flex-col"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {minutes.map((minute) => (
                <div
                  key={minute}
                  data-minute={minute}
                  onClick={() => selectMinute(minute)}
                  className={`
                    flex-shrink-0 p-3 h-10 flex items-center justify-center cursor-pointer
                    ${
                      currentMinute === minute
                        ? "bg-indigo-500 text-white font-medium"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/70"
                    }
                  `}
                >
                  {minute.toString().padStart(2, "0")}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      <div
        className="relative cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        {baseComponent ? (
          isValidElement(baseComponent) ? (
            cloneElement(baseComponent as ReactElement, {
              onClick: () => setIsOpen(!isOpen),
            })
          ) : (
            baseComponent
          )
        ) : (
          <>
            <Input
              type="text"
              readOnly
              placeholder={placeholder}
              value={
                selectedDate
                  ? format(selectedDate, "PPP HH:mm", { locale })
                  : ""
              }
              classNames={{
                input:
                  "w-full p-2 border rounded-md bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-dark-400 cursor-pointer",
              }}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg
                className="w-5 h-5 text-gray-500 dark:text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </div>
          </>
        )}
      </div>

      {isOpen && (
        <div
          className={`absolute z-50 mt-1 shadow-lg rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 p-3 
          ${isMobile ? "w-[calc(100vw-24px)] left-1/2 transform -translate-x-1/2 max-h-[90vh] overflow-y-auto" : "min-w-[500px]"}
          animate-in fade-in`}
        >
          <div
            className={`${isMobile ? "flex flex-col" : "grid grid-cols-[1fr,200px]"} gap-4`}
          >
            {/* Calendar Column */}
            <div className={isMobile ? "max-w-full overflow-x-auto" : ""}>
              <div className="flex justify-between items-center mb-3">
                <button
                  onClick={previousMonth}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <svg
                    className="w-5 h-5 text-gray-600 dark:text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <Text variant="h5" className="text-center">
                  {format(currentMonth, "MMMM yyyy", { locale })}
                </Text>
                <button
                  onClick={nextMonth}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <svg
                    className="w-5 h-5 text-gray-600 dark:text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              {renderDays()}
            </div>

            {/* Time Column */}
            <div
              className={
                isMobile
                  ? "mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
                  : "border-l border-gray-200 dark:border-gray-700 pl-4"
              }
            >
              {renderTimeSelector()}
            </div>
          </div>

          <div className="flex justify-end mt-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button
              size={isMobile ? "md" : "sm"}
              variant="outline"
              fullWidth={isMobile}
              onClick={() => {
                setIsOpen(false);
                if (onFinish && selectedDate) {
                  onFinish(selectedDate);
                }
              }}
              className="border-gray-300 dark:border-gray-600"
            >
              {t("common.finish")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
